import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
} from '@tanstack/react-router';

import { DesktopGate } from './layout/DesktopGate.tsx';
import { AppLayout } from './layout/AppShell.tsx';
import { NotFoundPage } from './pages/PlaceholderPage.tsx';
import { auditSearch } from './pages/audit/search.ts';
import { LoginPage } from './pages/auth/LoginPage.tsx';
import { loginSearch } from './pages/auth/loginSearch.ts';
import { ledgerSearch, statementSearch } from './pages/ledger/search.ts';

// In Dob Desktop every screen waits until the PC is registered (the first-run wizard, D120).
const rootRoute = createRootRoute({
  component: () => (
    <DesktopGate>
      <Outlet />
    </DesktopGate>
  ),
  notFoundComponent: NotFoundPage,
});

// The sign-in screen and the shell load first; every other screen is downloaded when it is first
// opened (or when a link to it is hovered), so the first visit stays fast on a mobile network.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearch,
  component: LoginPage,
});
const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  component: lazyRouteComponent(() => import('./pages/setup/SetupPage.tsx'), 'SetupPage'),
});
// Dob Desktop's hub status (M7-3): readable without signing in on the hub PC.
const hubRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/hub',
  component: lazyRouteComponent(() => import('./pages/hub/HubStatusPage.tsx'), 'HubStatusPage'),
});
// An ordinary PC's connection to its hub (M7-4): also readable without signing in.
const connectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connection',
  component: lazyRouteComponent(() => import('./pages/hub/ConnectionPage.tsx'), 'ConnectionPage'),
});
// Dob Desktop's printers (M7-6): readable without signing in, like the hub status page.
const printersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/printers',
  component: lazyRouteComponent(
    () => import('./pages/printers/DesktopPrintersPage.tsx'),
    'DesktopPrintersPage',
  ),
});
// The office Backup Agent's page (M7-8): the home of that PC, without signing in.
const backupAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backup-agent',
  component: lazyRouteComponent(
    () => import('./pages/backupAgent/BackupAgentPage.tsx'),
    'BackupAgentPage',
  ),
});
// Dob Desktop's updates (M7-7): readable without signing in, like the hub status page.
const updatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/updates',
  component: lazyRouteComponent(() => import('./pages/updates/UpdatesPage.tsx'), 'UpdatesPage'),
});
// Dob Desktop's offline session home (M7-5): outside the online shell (no server needed).
const offlineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline',
  component: lazyRouteComponent(() => import('./pages/offline/OfflinePage.tsx'), 'OfflinePage'),
});
// Dob Desktop signed in without internet (W4-6): this PC's stock, from its own copy.
const offlineStockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline/stock',
  component: lazyRouteComponent(
    () => import('./pages/inventory/StockPage.tsx'),
    'OfflineStockPage',
  ),
});
// …and its waste (W4-7): recorded on this PC, sent once when there is internet.
const offlineWasteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline/waste',
  component: lazyRouteComponent(
    () => import('./pages/inventory/DesktopWaste.tsx'),
    'OfflineWastePage',
  ),
});
// …and its purchasing (W5-4): an invoice and a blind count, saved on this PC with their photos.
const offlinePurchasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offline/purchases',
  component: lazyRouteComponent(
    () => import('./pages/purchases/OfflinePurchases.tsx'),
    'OfflinePurchasesPage',
  ),
});
const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(
    () => import('./pages/auth/ForgotPasswordPage.tsx'),
    'ForgotPasswordPage',
  ),
});

/** Everything behind sign-in shares the shell (sidebar, top bar, Emergency banner). */
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppLayout });

const page = <P extends string>(path: P, component: ReturnType<typeof lazyRouteComponent>) =>
  createRoute({ getParentRoute: () => appRoute, path, component });

// The ledger keeps its book, tab and dates in the address bar (links, reloads, back button).
const ledgerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger',
  validateSearch: ledgerSearch,
  component: lazyRouteComponent(() => import('./pages/ledger/LedgerPage.tsx'), 'LedgerPage'),
});
// The audit log keeps its filters in the address bar (links from other screens, reloads).
const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/audit',
  validateSearch: auditSearch,
  component: lazyRouteComponent(() => import('./pages/audit/AuditPage.tsx'), 'AuditPage'),
});
const statementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/ledger/accounts/$accountId',
  validateSearch: statementSearch,
  component: lazyRouteComponent(() => import('./pages/ledger/StatementPage.tsx'), 'StatementPage'),
});

// A new invoice may start from a cancelled one ("record it again", W5-2): ?replaces=<its id>; or
// from a receipt's excess (an extra invoice, W5-3): ?excess=<the receipt's id>.
const newInvoiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/purchases/invoices/new',
  validateSearch: (search: Record<string, unknown>): { replaces?: string; excess?: string } => ({
    ...(typeof search['replaces'] === 'string' ? { replaces: search['replaces'] } : {}),
    ...(typeof search['excess'] === 'string' ? { excess: search['excess'] } : {}),
  }),
  component: lazyRouteComponent(
    () => import('./pages/purchases/NewInvoicePage.tsx'),
    'NewInvoicePage',
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupRoute,
  hubRoute,
  connectionRoute,
  printersRoute,
  updatesRoute,
  backupAgentRoute,
  offlineRoute,
  offlineStockRoute,
  offlineWasteRoute,
  offlinePurchasesRoute,
  forgotRoute,
  appRoute.addChildren([
    page(
      '/cash',
      lazyRouteComponent(() => import('./pages/cash/CashPage.tsx'), 'CashPage'),
    ),
    page(
      '/',
      lazyRouteComponent(() => import('./pages/DashboardPage.tsx'), 'DashboardPage'),
    ),
    page(
      '/inbox',
      lazyRouteComponent(() => import('./pages/InboxPage.tsx'), 'InboxPage'),
    ),
    page(
      '/users',
      lazyRouteComponent(() => import('./pages/users/UsersPage.tsx'), 'UsersPage'),
    ),
    page(
      '/users/$userId',
      lazyRouteComponent(() => import('./pages/users/UserDetailPage.tsx'), 'UserDetailPage'),
    ),
    page(
      '/locations',
      lazyRouteComponent(() => import('./pages/LocationsPage.tsx'), 'LocationsPage'),
    ),
    page(
      '/locations/$locationId/printers',
      lazyRouteComponent(
        () => import('./pages/printers/LocationPrintersPage.tsx'),
        'LocationPrintersPage',
      ),
    ),
    page(
      '/account',
      lazyRouteComponent(() => import('./pages/AccountPage.tsx'), 'AccountPage'),
    ),
    page(
      '/devices',
      lazyRouteComponent(() => import('./pages/DevicesPage.tsx'), 'DevicesPage'),
    ),
    page(
      '/messages',
      lazyRouteComponent(() => import('./pages/MessagesPage.tsx'), 'MessagesPage'),
    ),
    page(
      '/health',
      lazyRouteComponent(() => import('./pages/HealthPage.tsx'), 'HealthPage'),
    ),
    page(
      '/emergency',
      lazyRouteComponent(() => import('./pages/EmergencyPage.tsx'), 'EmergencyPage'),
    ),
    page(
      '/approvals',
      lazyRouteComponent(() => import('./pages/approvals/ApprovalsPage.tsx'), 'ApprovalsPage'),
    ),
    page(
      '/approvals/$approvalId',
      lazyRouteComponent(
        () => import('./pages/approvals/ApprovalDetailPage.tsx'),
        'ApprovalDetailPage',
      ),
    ),
    page(
      '/sync-issues',
      lazyRouteComponent(() => import('./pages/syncIssues/SyncIssuesPage.tsx'), 'SyncIssuesPage'),
    ),
    ledgerRoute,
    page(
      '/ledger/entries/$entryId',
      lazyRouteComponent(() => import('./pages/ledger/EntryPage.tsx'), 'EntryPage'),
    ),
    statementRoute,
    auditRoute,
    page(
      '/settings',
      lazyRouteComponent(() => import('./pages/settings/SettingsPage.tsx'), 'SettingsPage'),
    ),
    page(
      '/inventory/items',
      lazyRouteComponent(() => import('./pages/inventory/ItemsPage.tsx'), 'ItemsPage'),
    ),
    page(
      '/inventory/items/$itemId',
      lazyRouteComponent(() => import('./pages/inventory/ItemDetailPage.tsx'), 'ItemDetailPage'),
    ),
    page(
      '/inventory/stock',
      lazyRouteComponent(() => import('./pages/inventory/StockPage.tsx'), 'StockPage'),
    ),
    page(
      '/inventory/waste',
      lazyRouteComponent(() => import('./pages/inventory/WastePage.tsx'), 'WastePage'),
    ),
    page(
      '/inventory/waste/$wasteId',
      lazyRouteComponent(() => import('./pages/inventory/WasteDetailPage.tsx'), 'WasteDetailPage'),
    ),
    page(
      '/inventory/openings',
      lazyRouteComponent(() => import('./pages/inventory/OpeningsPage.tsx'), 'OpeningsPage'),
    ),
    page(
      '/inventory/openings/$openingId',
      lazyRouteComponent(
        () => import('./pages/inventory/OpeningDetailPage.tsx'),
        'OpeningDetailPage',
      ),
    ),
    page(
      '/inventory/match',
      lazyRouteComponent(() => import('./pages/inventory/StockMatchCard.tsx'), 'StockMatchPage'),
    ),
    page(
      '/inventory/categories',
      lazyRouteComponent(() => import('./pages/inventory/CategoriesPage.tsx'), 'CategoriesPage'),
    ),
    page(
      '/purchases/suppliers',
      lazyRouteComponent(() => import('./pages/purchases/SuppliersPage.tsx'), 'SuppliersPage'),
    ),
    page(
      '/purchases/suppliers/$supplierId',
      lazyRouteComponent(
        () => import('./pages/purchases/SupplierDetailPage.tsx'),
        'SupplierDetailPage',
      ),
    ),
    page(
      '/purchases/suppliers/$supplierId/pay',
      lazyRouteComponent(() => import('./pages/purchases/PayPage.tsx'), 'PayPage'),
    ),
    page(
      '/purchases/invoices',
      lazyRouteComponent(() => import('./pages/purchases/InvoicesPage.tsx'), 'InvoicesPage'),
    ),
    newInvoiceRoute,
    page(
      '/purchases/receive',
      lazyRouteComponent(() => import('./pages/purchases/ReceivePage.tsx'), 'ReceiveTasksPage'),
    ),
    page(
      '/purchases/receive/$invoiceId',
      lazyRouteComponent(() => import('./pages/purchases/ReceivePage.tsx'), 'ReceiveInvoicePage'),
    ),
    page(
      '/purchases/excess',
      lazyRouteComponent(() => import('./pages/purchases/ExcessPage.tsx'), 'ExcessPage'),
    ),
    page(
      '/purchases/invoices/$invoiceId',
      lazyRouteComponent(
        () => import('./pages/purchases/InvoiceDetailPage.tsx'),
        'InvoiceDetailPage',
      ),
    ),
    page(
      '/roles',
      lazyRouteComponent(() => import('./pages/roles/RolesPage.tsx'), 'RolesPage'),
    ),
    page(
      '/roles/$code',
      lazyRouteComponent(() => import('./pages/roles/RoleDetailPage.tsx'), 'RoleDetailPage'),
    ),
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
